import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { temPermissao } from '../utils/permissoes';
import ObraDetalhe from './obraDetalhe';
import AjudaTooltip from '../components/AjudaTooltip';
import ModalConfirmacao from '../components/ModalConfirmacao';

function formatarDataLocal(v) {
  if (!v) return '—';
  const s = typeof v === 'string' ? v : v instanceof Date ? v.toISOString() : String(v);
  const [y, m, d] = s.split('T')[0].split('-');
  if (!y || !m || !d) return '—';
  return `${d}/${m}/${y}`;
}

function Obras({ origemNavegacao }) {
  const { id: idFromRoute } = useParams();
  const navigate = useNavigate();
  const [obras, setObras] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obraIdSelecionada, setObraIdSelecionada] = useState(() => (idFromRoute ? parseInt(idFromRoute) : null));
  const [mostrarForm, setMostrarForm] = useState(false);
  const [form, setForm] = useState({ clienteid: '', nome_obra: '' });
  const [filtroColuna, setFiltroColuna] = useState('nome_obra');
  const [filtroInput, setFiltroInput] = useState('');
  const [filtroAplicado, setFiltroAplicado] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [confirmacao, setConfirmacao] = useState({ aberto: false, id: null, nome: '' });
  const [duplicando, setDuplicando] = useState(null);
  const [formDuplicar, setFormDuplicar] = useState({ nome_obra: '', clienteid: '' });
  const [origemLocal, setOrigemLocal] = useState(null);
  const [creditoPrompt, setCreditoPrompt] = useState(null); // { saldoCreditos, precoCredito, onUsar }

  function pesquisar() { setFiltroAplicado(filtroInput); }

  async function carregarObras() {
    const response = await api.get('/obras');
    setObras(response.data);
  }

  async function carregarClientes() {
    const response = await api.get('/clientes?tipos=cliente');
    setClientes(response.data);
  }

  useEffect(() => {
    if (idFromRoute) setOrigemLocal(origemNavegacao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFromRoute]);

  useEffect(() => {
    carregarObras();
    carregarClientes();
  }, []);

  useEffect(() => {
    const id = idFromRoute ? parseInt(idFromRoute) : null;
    setObraIdSelecionada(id);
    if (!id) carregarObras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idFromRoute]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  }

  async function criarObra(usar_credito) {
    if (!form.clienteid) { toast.error('Selecione um cliente!'); return; }
    if (!form.nome_obra.trim()) { toast.error('Nome da obra é obrigatório!'); return; }
    try {
      const res = await api.post('/obras', usar_credito ? { ...form, usar_credito: true } : form);
      setForm({ clienteid: '', nome_obra: '' });
      setMostrarForm(false);
      carregarObras();
      toast.success(res.data?.mensagem || 'Obra cadastrada com sucesso!');
      setCreditoPrompt(null);
    } catch (err) {
      const data = err.response?.data;
      if (data?.pode_usar_credito !== undefined) {
        setCreditoPrompt({
          saldoCreditos: data.saldo_creditos,
          precoCredito: data.preco_credito,
          mensagem: data.mensagem,
          onUsar: data.saldo_creditos > 0 ? () => criarObra(true) : null,
        });
        return;
      }
      toast.error(data?.mensagem || 'Erro ao cadastrar obra.', { duration: 5000 });
    }
  }

  async function deletarObra(id) {
    try {
      await api.delete(`/obras/${id}`);
      carregarObras();
      toast.success('Obra removida com sucesso!');
    } catch (err) {
      const msg = err.response?.data?.mensagem;
      toast.error(msg || 'Não foi possível remover esta obra.', { duration: 5000 });
    }
  }

  function pedirConfirmacaoDeletar(obra) {
    setConfirmacao({ aberto: true, id: obra.obraid, nome: obra.nome_obra });
  }

  async function confirmarDeletar() {
    await deletarObra(confirmacao.id);
    setConfirmacao({ aberto: false, id: null, nome: '' });
  }

  function abrirDuplicar(o) {
    setFormDuplicar({ nome_obra: `Cópia de ${o.nome_obra}`, clienteid: o.clienteid });
    setDuplicando(o);
  }

  async function confirmarDuplicar(usar_credito) {
    if (!formDuplicar.nome_obra.trim()) { toast.error('Informe o nome da nova obra!'); return; }
    if (!formDuplicar.clienteid) { toast.error('Selecione um cliente!'); return; }
    try {
      const res = await api.post(`/obras/${duplicando.obraid}/duplicar`, usar_credito ? { ...formDuplicar, usar_credito: true } : formDuplicar);
      toast.success(res.data?.mensagem || 'Obra duplicada com sucesso!');
      setDuplicando(null);
      setCreditoPrompt(null);
      navigate(`/obras/${res.data.obraid}`);
    } catch (err) {
      const data = err.response?.data;
      if (data?.pode_usar_credito !== undefined) {
        setCreditoPrompt({
          saldoCreditos: data.saldo_creditos,
          precoCredito: data.preco_credito,
          mensagem: data.mensagem,
          onUsar: data.saldo_creditos > 0 ? () => confirmarDuplicar(true) : null,
        });
        return;
      }
      toast.error(data?.mensagem || 'Erro ao duplicar obra.');
    }
  }

  function labelStatus(status) {
    const labels = {
      orcamento: 'Orçamento',
      em_andamento: 'Em Andamento',
      em_andamento_alerta: 'Em Andamento',
      atrasada: 'Atrasada',
      concluida: 'Obra Entregue',
      concluida_pgto_pendente: 'Entregue · Pgto. Pendente',
      finalizada: 'Obra Finalizada',
      cancelada: 'Cancelada',
      pausada: 'Pausada',
      orcamento_recusado: 'Orç. Recusado',
    };
    return labels[status] || status;
  }

  function renderBadge(obra) {
    const status = obra.status_calculado || obra.status_obra;
    const dias = obra.dias_restantes;

    const configs = {
      orcamento: { classe: 'badge badge-orcamento' },
      em_andamento: { classe: 'badge badge-andamento' },
      em_andamento_alerta: { classe: 'badge badge-alerta', extra: ` · ${dias} dia${dias === 1 ? '' : 's'}` },
      atrasada: { classe: 'badge badge-atrasada' },
      concluida: { classe: 'badge badge-concluida' },
      concluida_pgto_pendente: { classe: 'badge badge-pgto-pendente' },
      finalizada: { classe: 'badge badge-concluida' },
      cancelada: { classe: 'badge badge-cancelada' },
      pausada: { classe: 'badge badge-pausada' },
      orcamento_recusado: { classe: 'badge badge-orcamento-recusado' },
    };

    const config = configs[status] || { classe: 'badge' };
    return <span className={config.classe}>{labelStatus(status)}{config.extra || ''}</span>;
  }

  const obrasFiltradas = obras.filter(o => {
    if (filtroStatus && (o.status_calculado || o.status_obra) !== filtroStatus) return false;
    if (filtroAplicado && !String(o[filtroColuna] || '').toLowerCase().includes(filtroAplicado.toLowerCase())) return false;
    return true;
  });

  if (obraIdSelecionada) {
    return (
      <ObraDetalhe
        key={obraIdSelecionada}
        obraid={obraIdSelecionada}
        listaObras={obrasFiltradas.map(o => ({ id: o.obraid, nome: o.nome_obra }))}
        onVoltar={() => navigate(origemLocal ? `/${origemLocal}` : '/obras')}
      />
    );
  }

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ margin: 0 }}>🏗️ Obras</h2>
          <AjudaTooltip texto={`Gerencie todas as obras do escritório.\n\nFluxo de status:\n• Orçamento → Em Andamento → Obra Entregue → Obra Finalizada\n• Atrasada: prazo ultrapassado\n\nServiços:\nNa fase de Orçamento, adicione serviços sem precisar informar datas. As datas de início e fim só são exigidas ao iniciar a obra.\n\nMateriais:\nEm qualquer fase você adiciona materiais (SINAPI ou manual). Após iniciar a obra, aparece uma área para registrar Fornecedor, Valor de Compra, Data de Compra e Data de Entrega de cada material.\n\nClique em uma linha para ver os detalhes da obra.`} />
        </div>
        <button className="btn-primary" onClick={() => setMostrarForm(!mostrarForm)}>
          {mostrarForm ? 'Cancelar' : '+ Nova Obra'}
        </button>
      </div>

      {mostrarForm && (
        <div className="card">
          <h3>Nova Obra</h3>
          <select name="clienteid" value={form.clienteid} onChange={handleChange}>
            <option value="">Selecione um cliente *</option>
            {clientes.map((c) => (
              <option key={c.clienteid} value={c.clienteid}>{c.nome_cliente}</option>
            ))}
          </select>
          <input name="nome_obra" placeholder="Nome da obra * (Ex: Residência João Silva - Área de Lazer)" value={form.nome_obra} onChange={handleChange} />
          <button className="btn-primary" onClick={() => criarObra(false)}>Cadastrar Obra</button>
        </div>
      )}

      <div className="card">
        <h3>Obras Cadastradas</h3>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          <select value={filtroColuna} onChange={e => { setFiltroColuna(e.target.value); setFiltroInput(''); setFiltroAplicado(''); }} style={{ minWidth: 130, marginBottom: 0 }}>
            <option value="obraid">Cód. Obra</option>
            <option value="nome_obra">Nome</option>
            <option value="nome_cliente">Cliente</option>
          </select>
          <input placeholder="Pesquisar..." value={filtroInput} onChange={e => setFiltroInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && pesquisar()} style={{ flex: 1, minWidth: 140, marginBottom: 0 }} />
          <button className="btn-primary" onClick={pesquisar} style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Pesquisar</button>
          <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} style={{ minWidth: 160, flex: 1, marginBottom: 0 }}>
            <option value="">Todos os Status</option>
            <option value="orcamento">Orçamento</option>
            <option value="em_andamento">Em Andamento</option>
            <option value="em_andamento_alerta">Em Andamento (Alerta)</option>
            <option value="atrasada">Atrasada</option>
            <option value="pausada">Pausada</option>
            <option value="concluida">Obra Entregue</option>
            <option value="concluida_pgto_pendente">Entregue · Pgto. Pendente</option>
            <option value="finalizada">Obra Finalizada</option>
            <option value="cancelada">Cancelada</option>
            <option value="orcamento_recusado">Orç. Recusado</option>
          </select>
        </div>

        <table>
          <thead>
            <tr>
              <th>Cód. Obra</th>
              <th>Nome</th>
              <th>Cliente</th>
              <th>Status</th>
              <th>Prazo</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {obrasFiltradas.map((o) => (
              <tr key={o.obraid} style={{ cursor: 'pointer' }} onClick={() => navigate(`/obras/${o.obraid}`)}>
                <td data-label="Cód. Obra">{o.obraid}</td>
                <td data-label="Nome">
                  <div>{o.nome_obra}</div>
                  {(o.cidade || o.estado) && (
                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                      📍 {[o.cidade, o.estado].filter(Boolean).join('/')}
                    </div>
                  )}
                </td>
                <td data-label="Cliente">{o.nome_cliente}</td>
                <td data-label="Status">{renderBadge(o)}</td>
                <td data-label="Prazo">{formatarDataLocal(o.prazo_combinado)}</td>
                <td data-label="Ações">
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); abrirDuplicar(o); }}>
                      Duplicar
                    </button>
                    {temPermissao('obras_excluir') && (
                      <button className="btn-danger" onClick={(e) => { e.stopPropagation(); pedirConfirmacaoDeletar(o); }}>
                        Deletar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {obrasFiltradas.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>Nenhuma obra encontrada</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <ModalConfirmacao
        aberto={confirmacao.aberto}
        titulo="Excluir obra"
        mensagem={`Tem certeza que deseja excluir a obra "${confirmacao.nome}"? Esta ação é irreversível e removerá todos os serviços e histórico vinculados.`}
        onConfirmar={confirmarDeletar}
        onCancelar={() => setConfirmacao({ aberto: false, id: null, nome: '' })}
      />

      {duplicando && (
        <div className="modal-overlay" onClick={() => setDuplicando(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Duplicar Obra</h3>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              Será criada uma nova obra em <strong>orçamento</strong> com todos os serviços copiados, sem histórico ou pagamentos.
            </p>
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Nome da nova obra *</label>
            <input
              value={formDuplicar.nome_obra}
              onChange={e => setFormDuplicar({ ...formDuplicar, nome_obra: e.target.value })}
              placeholder="Nome da nova obra"
            />
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Cliente *</label>
            <select
              value={formDuplicar.clienteid}
              onChange={e => setFormDuplicar({ ...formDuplicar, clienteid: e.target.value })}
            >
              <option value="">Selecione um cliente</option>
              {clientes.map(c => (
                <option key={c.clienteid} value={c.clienteid}>{c.nome_cliente}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
              <button className="btn-secondary" onClick={() => setDuplicando(null)}>Cancelar</button>
              <button className="btn-primary" onClick={() => confirmarDuplicar(false)}>Duplicar Obra</button>
            </div>
          </div>
        </div>
      )}

      {creditoPrompt && (
        <div className="modal-overlay" onClick={() => setCreditoPrompt(null)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>Limite de obras atingido</h3>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>{creditoPrompt.mensagem}</p>
            {creditoPrompt.onUsar ? (
              <>
                <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 16 }}>
                  Você tem <strong>{creditoPrompt.saldoCreditos}</strong> crédito{creditoPrompt.saldoCreditos > 1 ? 's' : ''} avulso{creditoPrompt.saldoCreditos > 1 ? 's' : ''} {creditoPrompt.saldoCreditos > 1 ? 'disponíveis' : 'disponível'}. Deseja usar 1 crédito para liberar esta obra?
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={() => setCreditoPrompt(null)}>Cancelar</button>
                  <button className="btn-primary" onClick={creditoPrompt.onUsar}>Usar 1 crédito</button>
                </div>
              </>
            ) : (
              <>
                <p style={{ fontSize: 14, color: 'var(--text-primary)', marginBottom: 16 }}>
                  Você não possui créditos avulsos. Cada crédito custa R$ {parseFloat(creditoPrompt.precoCredito || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} e libera 1 obra extra, permanentemente. Compre na aba "Plano".
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="btn-secondary" onClick={() => setCreditoPrompt(null)}>Fechar</button>
                  <button className="btn-primary" onClick={() => { setCreditoPrompt(null); navigate('/plano'); }}>Ir para Plano</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Obras;
